/**
 * WebAuthn (passkey) encoding helpers.
 *
 * The backend sends all binary WebAuthn fields as base64url strings inside the
 * options JSON and expects the returned credential to use base64url too. The
 * browser WebAuthn API works with ArrayBuffer. Convert at the boundary.
 */

// base64url string -> ArrayBuffer
export function b64urlToBuf(b64url) {
  const pad = "=".repeat((4 - (b64url.length % 4)) % 4);
  const b64 = (b64url + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const buf = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) buf[i] = raw.charCodeAt(i);
  return buf.buffer;
}

// ArrayBuffer -> base64url string
export function bufToB64url(buf) {
  const bytes = new Uint8Array(buf);
  let str = "";
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// Rebuild navigator.credentials.create options from backend JSON
export function prepCreate(options) {
  options.challenge = b64urlToBuf(options.challenge);
  options.user.id = b64urlToBuf(options.user.id);
  (options.excludeCredentials || []).forEach((c) => {
    c.id = b64urlToBuf(c.id);
  });
  return options;
}

// Rebuild navigator.credentials.get options from backend JSON
export function prepGet(options) {
  options.challenge = b64urlToBuf(options.challenge);
  (options.allowCredentials || []).forEach((c) => {
    c.id = b64urlToBuf(c.id);
  });
  return options;
}

// Registration credential -> JSON string the backend can parse
export function regCredentialToJson(cred) {
  return JSON.stringify({
    type: cred.type,
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    response: {
      clientDataJSON: bufToB64url(cred.response.clientDataJSON),
      attestationObject: bufToB64url(cred.response.attestationObject),
      transports: cred.response.getTransports ? cred.response.getTransports() : [],
    },
    clientExtensionResults: cred.getClientExtensionResults(),
  });
}

// Assertion (login) credential -> JSON string the backend can parse
export function loginCredentialToJson(cred) {
  return JSON.stringify({
    type: cred.type,
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    response: {
      clientDataJSON: bufToB64url(cred.response.clientDataJSON),
      authenticatorData: bufToB64url(cred.response.authenticatorData),
      signature: bufToB64url(cred.response.signature),
      userHandle: cred.response.userHandle
        ? bufToB64url(cred.response.userHandle)
        : null,
    },
    clientExtensionResults: cred.getClientExtensionResults(),
  });
}

// Feature-detect: WebAuthn requires a secure context (https or localhost)
export function passkeysSupported() {
  return !!window.PublicKeyCredential;
}
